import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  Collapse,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  EditOutlined as EditIcon,
  AttachFileOutlined as FileIcon,
  DeleteOutline as DeleteIcon,
  DownloadOutlined as DownloadIcon,
  PrecisionManufacturing as MachineIcon,
  PersonOutline as PersonIcon,
  RepeatOutlined as RecurringIcon,
  OpenInNewOutlined as LinkIcon,
  LaunchOutlined,
  LocationOnOutlined as LocationIcon,
  PersonRemoveOutlined as PersonRemoveIcon,
  CalendarTodayOutlined as CalendarIcon,
  AddOutlined as AddIcon,
  AccountTreeOutlined as TreeIcon,
  Check as CheckIcon,
  PauseOutlined as PauseIcon,
  PlayArrowOutlined as PlayIcon,
  GroupsOutlined as TeamIcon,
  DescriptionOutlined as SrIcon,
  ExpandMore as ExpandMoreIcon,
  CloseOutlined as CloseIcon,
} from "@mui/icons-material";
import { useParams, useNavigate, Link } from "react-router-dom";
import EscalateDialog from "./workBoard/EscalateDialog";
import CompletionDialog from "./workBoard/CompletionDialog";
import ReworkDialog from "./workBoard/ReworkDialog";
import CreateWorkOrderPanel from "./workBoard/CreateWorkOrderPanel";
import SrQuickViewPanel from "./serviceReports/SrQuickViewPanel";
import IncidentContextCard from "./detail/IncidentContextCard";
import { useToastStack, ToastStack } from "./serviceReports/SrActionToast";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  fetchWorkOrder,
  fetchTeams,
  changeWorkOrderStatus,
  addWorkOrderProgressUpdate,
  clearCurrentWorkOrder,
  editCompletionDetails,
  escalateWorkOrder,
  regenerateFutureInstances,
  cancelFutureInstances,
  removeAssignee,
  updateWorkOrder,
} from "../store/workOrdersSlice";
import workOrdersApi, { getWorkOrderFileUrl } from "../api/workOrdersApi";
import { ImagePreviewStrip } from "../../../shared/components/gallery";
import { useGalleryImages } from "../../../shared/components/gallery/useGalleryImages";
import {
  useAuthenticatedBlobUrls,
  downloadAuthenticatedFile,
} from "../../../shared/hooks/useAuthenticatedBlobUrl";
import { dt } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import useDataRefresh from "../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../shared/hooks/useVisibilityRefresh";
import usePermission from "../../../shared/hooks/usePermission";
import { Modules } from "../../../core/models/PermissionModels";
import useTranslation from "../../../shared/hooks/useTranslation";
import accountContext from "../../../account/accountContext";
import { Roles } from "../../../account/models/AccountModels";
import Loader from "../../../shared/components/Loader";
import { formatFileSize } from "../../../shared/models/models";
import WorkOrderStatusBadge, { woStatusColors } from "./WorkOrderStatusBadge";
import WorkOrderPriorityBadge from "./WorkOrderPriorityBadge";
import WorkOrderTypeBadge from "./WorkOrderTypeBadge";
import {
  WorkOrderStatusCode,
  ChangeWorkOrderStatus as ChangeStatusModel,
  UpdateWorkOrder,
  WorkOrderFile,
  isClosingRemarksFile,
} from "../models/models";
import dayjs from "dayjs";
import { RootState } from "../../../core/store";
import { allowedTransitions } from "../utils/statusTransitions";

// ─── Locked palette ────────────────────────────────────────
const p = {
  terracotta: "#D4572B",
  terracottaHover: "#B84A24",
  amber: "#E8A817",
  charcoal: "#0C1117",
  warmCream: "#F7F5F0",
  pageBg: "#F2F0EB",
  white: "#FFFFFF",
  border: "#E8E5DF",
  muted: "#8C9099",
  body: "#4A4F57",
  green: "#2D8C4E",
  blue: "#3B82F6",
  red: "#E74C3C",
};

// Translation-key maps — resolved via `translate()` at the render site so all
// action buttons are localized (en / cs / sk). Keys live under `actions.*` in
// operations.json.
const statusActionLabelKeys: Record<number, string> = {
  [WorkOrderStatusCode.Assigned]: "actions.assign",
  [WorkOrderStatusCode.Scheduled]: "actions.schedule",
  [WorkOrderStatusCode.InProgress]: "actions.startWork",
  [WorkOrderStatusCode.OnHold]: "actions.putOnHold",
  [WorkOrderStatusCode.Completed]: "actions.markComplete",
  [WorkOrderStatusCode.PendingReview]: "actions.markDone",
  [WorkOrderStatusCode.Cancelled]: "actions.cancelWorkOrder",
};

const statusActionLabelKeysPhone: Record<number, string> = {
  [WorkOrderStatusCode.Assigned]: "actions.assign",
  [WorkOrderStatusCode.Scheduled]: "actions.schedule",
  [WorkOrderStatusCode.InProgress]: "actions.startShort",
  [WorkOrderStatusCode.OnHold]: "actions.onHoldShort",
  [WorkOrderStatusCode.Completed]: "actions.completeShort",
  [WorkOrderStatusCode.PendingReview]: "actions.doneShort",
  [WorkOrderStatusCode.Cancelled]: "actions.cancelShort",
};

// ─── Tailwind button classes per target status ──────────────────────
const getActionButtonClass = (targetStatus: number): string => {
  const base = "w-full rounded-lg px-5 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer text-center";

  switch (targetStatus) {
    case WorkOrderStatusCode.PendingReview:
    case WorkOrderStatusCode.InProgress:
      return `${base} bg-[#D4572B] text-white hover:bg-[#B84A24] hover:shadow-md`;
    case WorkOrderStatusCode.Completed:
      return `${base} bg-[#2D8C4E] text-white hover:bg-[#1a6b3a] hover:shadow-md`;
    case WorkOrderStatusCode.OnHold:
      return `${base} bg-transparent text-[#E8A817] border-[1.5px] border-[#E8A817] hover:bg-[#E8A817]/5`;
    case WorkOrderStatusCode.Cancelled:
      return `${base} bg-transparent text-[#C0392B] font-medium hover:bg-[#C0392B]/5`;
    default:
      return `${base} bg-[#D4572B] text-white hover:bg-[#B84A24]`;
  }
};

const WorkOrderDetailPage = () => {
  useRedesignSetup();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("operations");
  const { canWrite } = usePermission(Modules.Maintenance);
  const { isPhone, isTablet, isDesktop } = useBreakpoint();

  const currentUserId = accountContext.user?.id;
  const isWorker = accountContext.user?.role === Roles.WORKER;
  /** Manager+ check: Admin or Manager — anyone who is NOT a Worker. */
  const canManage = !isWorker;

  const { currentWorkOrder: wo, currentLoading, teams } = useAppSelector(
    (state: RootState) => state.workOrders
  );

  const [updateText, setUpdateText] = useState("");
  const [completionNotes, setCompletionNotes] = useState("");
  const [cost, setCost] = useState("");
  const [createSR, setCreateSR] = useState(false);
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [showEscalateDialog, setShowEscalateDialog] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [showReworkDialog, setShowReworkDialog] = useState(false);
  // WO-WORKER-EDIT-NOTES: separate flag for re-opening CompletionDialog in
  // edit mode on a PendingReview WO. Keeping it distinct from
  // `showCompletionForm` avoids muddying the submit-for-review flow.
  const [showEditDetailsDialog, setShowEditDetailsDialog] = useState(false);

  // Edit panel state
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [editPanelWOId, setEditPanelWOId] = useState<string | undefined>(undefined);
  const [editPanelAllFuture, setEditPanelAllFuture] = useState(false);

  // SR Quick View panel state
  const [srQuickView, setSrQuickView] = useState<{
    open: boolean;
    serviceReportId: string;
    serviceReportNumber?: string;
  }>({ open: false, serviceReportId: "" });
  const { addToast, dismissToast, toasts } = useToastStack();

  // Remove confirmation dialog state
  const [removeDialog, setRemoveDialog] = useState<{
    type: "assignee" | "machine" | "location";
    userId?: string;
    name: string;
  } | null>(null);

  // Team name lookup (fallback if backend teamName is not set)
  const teamNameLookup = useMemo(() => {
    const map: Record<string, string> = {};
    (teams || []).forEach((t) => { map[t.id] = t.name; });
    return map;
  }, [teams]);

  // ─── Data loading ──────────────────────────────────────────
  const loadDetail = useCallback(() => {
    if (id) dispatch(fetchWorkOrder(id));
  }, [dispatch, id]);

  useEffect(() => {
    loadDetail();
    dispatch(fetchTeams());
    return () => { dispatch(clearCurrentWorkOrder()); };
  }, [loadDetail, dispatch]);

  useDataRefresh("workOrders", loadDetail);
  useVisibilityRefresh(loadDetail);

  // ─── Permission helpers ────────────────────────────────────
  const isOwner = wo?.createdByUserId === currentUserId;
  const isAssignee = wo?.assignedToUserId === currentUserId;
  const isAssignedToMe = useMemo(() => {
    if (!wo?.assignees || !currentUserId) return false;
    return wo.assignees.some((a: any) => a.userId === currentUserId);
  }, [wo?.assignees, currentUserId]);
  const canEditWO = canManage || isOwner || isAssignee;
  const canAssign = canManage;
  const canVerify = canManage;
  const canTransition = canWrite && (canManage || isOwner || isAssignee);

  // ─── Status change handler ────────────────────────────────
  const handleStatusChange = useCallback(
    async (newStatusId: number) => {
      if (!wo) return;
      if (newStatusId === WorkOrderStatusCode.PendingReview) {
        setShowCompletionForm(true);
        return;
      }
      const model: ChangeStatusModel = {
        newStatusId,
        notes: undefined,
        createServiceReport: false,
      };
      await dispatch(changeWorkOrderStatus({ id: wo.id, model }));
      loadDetail();
    },
    [wo, dispatch, loadDetail]
  );

  // ─── Completion dialog confirm handler ────────────────────
  const handleCompletionConfirm = useCallback(
    async (notes: string, costVal: number | undefined, createServiceReport: boolean) => {
      if (!wo) return;
      const model: ChangeStatusModel = {
        newStatusId: WorkOrderStatusCode.PendingReview,
        notes: undefined,
        completionNotes: notes || undefined,
        cost: costVal,
        createServiceReport,
      };
      await dispatch(changeWorkOrderStatus({ id: wo.id, model }));
      setShowCompletionForm(false);
      setCompletionNotes("");
      setCost("");
      setCreateSR(false);
      loadDetail();
    },
    [wo, dispatch, loadDetail]
  );

  // ─── Rework dialog confirm handler ─────────────────────────
  const handleReworkConfirm = useCallback(
    async (reworkNotes: string) => {
      if (!wo) return;
      const model: ChangeStatusModel = {
        newStatusId: WorkOrderStatusCode.InProgress,
        notes: reworkNotes || undefined,
        createServiceReport: false,
      };
      await dispatch(changeWorkOrderStatus({ id: wo.id, model }));
      setShowReworkDialog(false);
      loadDetail();
    },
    [wo, dispatch, loadDetail]
  );

  // ─── Edit closing details confirm handler (WO-WORKER-EDIT-NOTES) ────
  // Routes the 3-arg CompletionDialog onConfirm signature into the dedicated
  // PATCH endpoint. The `createSR` arg is ignored — the checkbox is hidden
  // in edit mode, and SR creation is a one-time event on submission.
  const handleEditDetailsConfirm = useCallback(
    async (notes: string, costVal: number | undefined) => {
      if (!wo) return;
      await dispatch(
        editCompletionDetails({ id: wo.id, completionNotes: notes, cost: costVal })
      );
      setShowEditDetailsDialog(false);
      loadDetail();
    },
    [wo, dispatch, loadDetail]
  );

  // ─── Progress update handler ──────────────────────────────
  const handleAddUpdate = useCallback(async () => {
    if (!wo || !updateText.trim()) return;
    await dispatch(addWorkOrderProgressUpdate({ id: wo.id, model: { updateText: updateText.trim() } }));
    setUpdateText("");
    loadDetail();
  }, [wo, dispatch, updateText, loadDetail]);

  // ─── File upload ──────────────────────────────────────────
  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!wo || !e.target.files?.[0]) return;
      await workOrdersApi.uploadFile(wo.id, e.target.files[0]);
      loadDetail();
    },
    [wo, loadDetail]
  );

  const handleFileDelete = useCallback(
    async (fileId: string) => {
      if (!wo) return;
      await workOrdersApi.deleteFile(wo.id, fileId);
      loadDetail();
    },
    [wo, loadDetail]
  );

  // ─── Escalation handler ──────────────────────────────────
  const terminalStatuses = [WorkOrderStatusCode.Completed, WorkOrderStatusCode.PendingReview, WorkOrderStatusCode.Cancelled];
  const canEscalate = canManage
    && wo
    && !terminalStatuses.includes(wo.workOrderStatusId)
    && !wo.serviceRequestId
    && !!wo.machineId;

  const handleEscalate = useCallback(async (issue: string, description: string) => {
    if (!wo) return;
    setEscalating(true);
    try {
      await dispatch(escalateWorkOrder({ id: wo.id, model: { issue, description } }));
      setShowEscalateDialog(false);
      loadDetail();
    } finally {
      setEscalating(false);
    }
  }, [wo, dispatch, loadDetail]);

  // ─── Edit panel helpers ─────────────────────────────────
  const openEditPanel = useCallback((woId?: string, allFuture?: boolean) => {
    setEditPanelWOId(woId || id);
    setEditPanelAllFuture(!!allFuture);
    setEditPanelOpen(true);
  }, [id]);

  const closeEditPanel = useCallback(() => {
    setEditPanelOpen(false);
    setEditPanelWOId(undefined);
    setEditPanelAllFuture(false);
  }, []);

  const handleEditPanelSaved = useCallback(() => {
    closeEditPanel();
    loadDetail(); // Refresh detail data
  }, [closeEditPanel, loadDetail]);

  const handleCancelAllFuture = useCallback(async () => {
    if (!wo?.recurrenceParentId) return;
    try {
      await dispatch(cancelFutureInstances(wo.recurrenceParentId)).unwrap();
      loadDetail();
    } catch { /* error handled by slice */ }
  }, [wo, dispatch, loadDetail]);

  // ─── SR Quick View handlers ────────────────────────────────
  const handleSrClick = useCallback((srId: string, srNumber?: string) => {
    setSrQuickView({ open: true, serviceReportId: srId, serviceReportNumber: srNumber });
  }, []);

  const handleSrQuickViewClose = useCallback(() => {
    setSrQuickView((prev) => ({ ...prev, open: false }));
  }, []);

  const handleSrUnlinked = useCallback((srNumber: string) => {
    setSrQuickView((prev) => ({ ...prev, open: false }));
    addToast("edit", `${srNumber} unlinked from ${wo?.number || "work order"}`);
    loadDetail();
  }, [wo?.number, loadDetail]);

  // ─── Inline remove handlers ──────────────────────────────
  const buildUpdateModelFromWO = useCallback((woData: any, overrides: Partial<UpdateWorkOrder>): UpdateWorkOrder => ({
    title: woData.title,
    description: woData.description || undefined,
    workInstructions: woData.workInstructions || undefined,
    workOrderTypeId: woData.workOrderTypeId,
    priorityCode: woData.priorityCode,
    machineId: woData.machineId || undefined,
    locationId: woData.locationId || undefined,
    dueDate: woData.dueDate || undefined,
    scheduledStartDate: woData.scheduledStartDate || undefined,
    scheduledEndDate: woData.scheduledEndDate || undefined,
    estimatedDurationMinutes: woData.estimatedDurationMinutes || undefined,
    teamId: woData.teamId || undefined,
    completionNotes: woData.completionNotes || undefined,
    cost: woData.cost ?? undefined,
    ...overrides,
  }), []);

  const handleRemoveAssignee = useCallback((userId: string, userName: string) => {
    if (!wo) return;
    setRemoveDialog({ type: "assignee", userId, name: userName });
  }, [wo]);

  const handleRemoveMachine = useCallback(() => {
    if (!wo) return;
    setRemoveDialog({ type: "machine", name: wo.machineName || "this machine" });
  }, [wo]);

  const handleRemoveLocation = useCallback(() => {
    if (!wo) return;
    setRemoveDialog({ type: "location", name: wo.locationName || "this location" });
  }, [wo]);

  const handleRemoveConfirm = useCallback(async () => {
    if (!wo || !removeDialog) return;
    if (removeDialog.type === "assignee" && removeDialog.userId) {
      await dispatch(removeAssignee({ workOrderId: wo.id, userId: removeDialog.userId }));
    } else if (removeDialog.type === "machine") {
      const model = buildUpdateModelFromWO(wo, { machineId: undefined });
      await dispatch(updateWorkOrder({ id: wo.id, model }));
    } else if (removeDialog.type === "location") {
      const model = buildUpdateModelFromWO(wo, { locationId: undefined });
      await dispatch(updateWorkOrder({ id: wo.id, model }));
    }
    setRemoveDialog(null);
    loadDetail();
  }, [wo, removeDialog, dispatch, loadDetail, buildUpdateModelFromWO]);

  // ─── Loading state ────────────────────────────────────────
  if (currentLoading && !wo) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "100px 0", backgroundColor: p.pageBg, flex: 1 }}>
        <Loader />
      </Box>
    );
  }

  if (!wo) {
    return (
      <Box sx={{ padding: "40px", textAlign: "center", fontFamily: dt.font, color: p.muted, backgroundColor: p.pageBg, flex: 1 }}>
        Work order not found.
      </Box>
    );
  }

  // Worker-allowed transitions: Start Work, Hold, Complete only (no Assign, Schedule, Cancel)
  const workerAllowedStatuses = [WorkOrderStatusCode.InProgress, WorkOrderStatusCode.OnHold, WorkOrderStatusCode.Completed];
  const allTransitions = allowedTransitions[wo.workOrderStatusId] || [];
  const transitions = canManage ? allTransitions : allTransitions.filter((t) => workerAllowedStatuses.includes(t));
  // Use backend teamName first, fallback to lookup from teams list
  const teamName = wo.teamName || (wo.teamId ? teamNameLookup[wo.teamId] : undefined);

  // ─── Render ────────────────────────────────────────────────
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        backgroundColor: p.pageBg,
        fontFamily: dt.font,
        overflowX: "hidden",
      }}
    >
      {/* ── Header bar ─────────────────────────────────────── */}
      <Box
        sx={{
          backgroundColor: p.white,
          borderBottom: `1px solid ${p.border}`,
          padding: isPhone ? "16px 16px 14px" : isTablet ? "20px 24px 18px" : "24px 40px 20px",
          width: "100%",
        }}
      >
        <Box sx={{ maxWidth: "1320px", margin: "0 auto" }}>
          {/* Back navigation */}
          <Box
            onClick={() => navigate(canManage ? "/operations/work-board" : "/operations/my-tasks")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 400,
              color: p.muted,
              cursor: "pointer",
              marginBottom: "12px",
              transition: "color 0.15s ease",
              "&:hover": { color: p.body },
            }}
          >
            <Box component="span" sx={{ fontSize: "16px", lineHeight: 1 }}>&lsaquo;</Box>
            {canManage ? "Work Board" : "My Tasks"}
          </Box>

          {/* WO number (mono, small, above title) */}
          <Box sx={{
            fontFamily: dt.fontMono,
            fontSize: "13px",
            fontWeight: 400,
            color: p.muted,
            letterSpacing: "0.02em",
            marginBottom: "4px",
          }}>
            {wo.number}
          </Box>

          {/* Title row */}
          <Box
            sx={{
              display: "flex",
              alignItems: isPhone ? "flex-start" : "center",
              flexDirection: isPhone ? "column" : "row",
              gap: isPhone ? "8px" : "16px",
              marginBottom: "12px",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{
                fontFamily: dt.font,
                fontSize: isPhone ? "20px" : isTablet ? "22px" : "24px",
                fontWeight: 700,
                color: p.charcoal,
                lineHeight: 1.25,
                letterSpacing: "-0.02em",
              }}>
                {wo.title}
              </Box>
            </Box>
          </Box>

          {/* Badges row */}
          <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            <WorkOrderTypeBadge typeId={wo.workOrderTypeId} typeName={wo.workOrderTypeName} size="lg" />
            <WorkOrderPriorityBadge priorityCode={wo.priorityCode} size="lg" />
            <WorkOrderStatusBadge statusId={wo.workOrderStatusId} statusName={wo.workOrderStatusName} size="lg" />
          </Box>
        </Box>
      </Box>

      {/* Terracotta accent line */}
      <Box sx={{ height: "2px", background: `linear-gradient(90deg, ${p.terracotta} 0%, ${p.terracotta}40 60%, transparent 100%)` }} />

      {/* ── Tablet info strip (below header) ────────────────── */}
      {isTablet && (
        <TabletInfoStrip wo={wo} teamName={teamName} translate={translate} />
      )}

      {/* ── Page body ───────────────────────────────────────── */}
      <Box sx={{ padding: isPhone ? "16px" : isTablet ? "20px 24px" : "24px 40px", maxWidth: "1320px", margin: "0 auto" }}>

        {/* ── Phone layout ────────────────────────────────── */}
        {isPhone ? (
          /* paddingBottom clears the 70px MIDAS bot so the last card isn't clipped. */
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0, paddingBottom: "90px" }}>
            {/* Actions — top, prominent */}
            <ActionsCard
              wo={wo}
              transitions={transitions}
              canTransition={canTransition}
              canVerify={canVerify}
              canWrite={canWrite}
              canManage={canManage}
              isAssignedToMe={isAssignedToMe}
              canEscalate={!!canEscalate}
              showCompletionForm={showCompletionForm}
              completionNotes={completionNotes}
              cost={cost}
              createSR={createSR}
              translate={translate}
              handleStatusChange={handleStatusChange}
              setShowCompletionForm={setShowCompletionForm}
              setCompletionNotes={setCompletionNotes}
              setCost={setCost}
              setCreateSR={setCreateSR}
              setShowEscalateDialog={setShowEscalateDialog}
              setShowReworkDialog={setShowReworkDialog}
              onEdit={() => openEditPanel(wo.id)}
              onEditClosingDetails={() => setShowEditDetailsDialog(true)}
              isPhone={isPhone}
            />
            {/* Closing Report */}
            <ClosingReportCard wo={wo} translate={translate} />
            {/* Description */}
            <DescriptionCard wo={wo} hasSrs={!!((wo.linkedServiceReports && wo.linkedServiceReports.length > 0) || wo.serviceReportId)} translate={translate} />
            {/* Work Instructions */}
            <WorkInstructionsCard wo={wo} translate={translate} />
            {/* Incident Context (tabbed SRs) */}
            <IncidentContextCard
              wo={wo}
              translate={translate}
              onViewFullReport={handleSrClick}
            />
            {/* Timeline */}
            <TimelineCard
              wo={wo}
              canWrite={canWrite}
              translate={translate}
              updateText={updateText}
              setUpdateText={setUpdateText}
              handleAddUpdate={handleAddUpdate}
            />
            {/* Details */}
            <DetailsCard wo={wo} teamName={teamName} navigate={navigate} isPhone={true} canManage={canManage} translate={translate} onRemoveAssignee={handleRemoveAssignee} onRemoveMachine={handleRemoveMachine} onRemoveLocation={handleRemoveLocation} />
            {/* Linked Items */}
            <LinkedItemsCard wo={wo} navigate={navigate} translate={translate} isPhone={isPhone} onSrClick={handleSrClick} />
            {/* Attachments */}
            <AttachmentsCard
              wo={wo}
              canWrite={canWrite}
              translate={translate}
              handleFileUpload={handleFileUpload}
              handleFileDelete={handleFileDelete}
            />
          </Box>
        ) : isTablet ? (
          /* ── Tablet: Single-column with info strip + accordion ── */
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
            {/* Actions — side-by-side buttons */}
            <ActionsCard
              wo={wo}
              transitions={transitions}
              canTransition={canTransition}
              canVerify={canVerify}
              canWrite={canWrite}
              canManage={canManage}
              isAssignedToMe={isAssignedToMe}
              canEscalate={!!canEscalate}
              showCompletionForm={showCompletionForm}
              completionNotes={completionNotes}
              cost={cost}
              createSR={createSR}
              translate={translate}
              handleStatusChange={handleStatusChange}
              setShowCompletionForm={setShowCompletionForm}
              setCompletionNotes={setCompletionNotes}
              setCost={setCost}
              setCreateSR={setCreateSR}
              setShowEscalateDialog={setShowEscalateDialog}
              setShowReworkDialog={setShowReworkDialog}
              onEdit={() => openEditPanel(wo.id)}
              onEditClosingDetails={() => setShowEditDetailsDialog(true)}
              isPhone={false}
              isTablet={true}
            />
            {/* Closing Report */}
            <ClosingReportCard wo={wo} translate={translate} />
            {/* Work Instructions */}
            <WorkInstructionsCard wo={wo} translate={translate} />
            {/* Incident Context (tabbed SRs) */}
            <IncidentContextCard
              wo={wo}
              translate={translate}
              onViewFullReport={handleSrClick}
            />
            {/* Description */}
            <DescriptionCard wo={wo} hasSrs={!!((wo.linkedServiceReports && wo.linkedServiceReports.length > 0) || wo.serviceReportId)} translate={translate} />
            {/* Timeline */}
            <TimelineCard
              wo={wo}
              canWrite={canWrite}
              translate={translate}
              updateText={updateText}
              setUpdateText={setUpdateText}
              handleAddUpdate={handleAddUpdate}
            />
            {/* ALL DETAILS accordion */}
            <AllDetailsAccordion
              wo={wo}
              teamName={teamName}
              navigate={navigate}
              translate={translate}
              canManage={canManage}
              canWrite={canWrite}
              onCancelAllFuture={handleCancelAllFuture}
              onEditAllFuture={(woId) => openEditPanel(woId, true)}
              onRemoveAssignee={handleRemoveAssignee}
              onRemoveMachine={handleRemoveMachine}
              onRemoveLocation={handleRemoveLocation}
            />
            {/* Linked Items */}
            <LinkedItemsCard wo={wo} navigate={navigate} translate={translate} isPhone={false} onSrClick={handleSrClick} />
            {/* Attachments */}
            <AttachmentsCard
              wo={wo}
              canWrite={canWrite}
              translate={translate}
              handleFileUpload={handleFileUpload}
              handleFileDelete={handleFileDelete}
            />
          </Box>
        ) : (
          /* ── Desktop: Two-column grid ─────────────────────── */
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1.857fr 1fr",
              gap: "24px",
            }}
          >
            {/* ── Left column ──────────────────────────────── */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", minWidth: 0 }}>
              <ClosingReportCard wo={wo} translate={translate} />
              <WorkInstructionsCard wo={wo} translate={translate} />
              <IncidentContextCard
                wo={wo}
                translate={translate}
                onViewFullReport={handleSrClick}
              />
              <DescriptionCard wo={wo} hasSrs={!!((wo.linkedServiceReports && wo.linkedServiceReports.length > 0) || wo.serviceReportId)} translate={translate} />
              <TimelineCard
                wo={wo}
                canWrite={canWrite}
                translate={translate}
                updateText={updateText}
                setUpdateText={setUpdateText}
                handleAddUpdate={handleAddUpdate}
              />
              <AttachmentsCard
                wo={wo}
                canWrite={canWrite}
                translate={translate}
                handleFileUpload={handleFileUpload}
                handleFileDelete={handleFileDelete}
              />
            </Box>

            {/* ── Right column (desktop) ───────────────────── */}
            <RightColumn
              wo={wo}
              transitions={transitions}
              canTransition={canTransition}
              canVerify={canVerify}
              canManage={canManage}
              canWrite={canWrite}
              isAssignedToMe={isAssignedToMe}
              canEscalate={!!canEscalate}
              showCompletionForm={showCompletionForm}
              completionNotes={completionNotes}
              cost={cost}
              createSR={createSR}
              teamName={teamName}
              translate={translate}
              navigate={navigate}
              handleStatusChange={handleStatusChange}
              setShowCompletionForm={setShowCompletionForm}
              setCompletionNotes={setCompletionNotes}
              setCost={setCost}
              setCreateSR={setCreateSR}
              setShowEscalateDialog={setShowEscalateDialog}
              setShowReworkDialog={setShowReworkDialog}
              onCancelAllFuture={handleCancelAllFuture}
              onEditAllFuture={(woId) => openEditPanel(woId, true)}
              onSrClick={handleSrClick}
              onEdit={() => openEditPanel(wo.id)}
              onEditClosingDetails={() => setShowEditDetailsDialog(true)}
              onRemoveAssignee={handleRemoveAssignee}
              onRemoveMachine={handleRemoveMachine}
              onRemoveLocation={handleRemoveLocation}
              isPhone={false}
            />
          </Box>
        )}
      </Box>

      {/* Edit Work Order side panel */}
      <CreateWorkOrderPanel
        open={editPanelOpen}
        onClose={closeEditPanel}
        onCreated={handleEditPanelSaved}
        editWorkOrderId={editPanelWOId}
        editAllFuture={editPanelAllFuture}
      />

      {/* Escalation dialog */}
      {wo && (
        <EscalateDialog
          open={showEscalateDialog}
          workOrder={wo}
          onConfirm={handleEscalate}
          onCancel={() => setShowEscalateDialog(false)}
          loading={escalating}
        />
      )}

      {/* SR Quick View side panel */}
      {wo && (
        <SrQuickViewPanel
          open={srQuickView.open}
          serviceReportId={srQuickView.serviceReportId}
          serviceReportNumber={srQuickView.serviceReportNumber}
          machineId={wo.machineId}
          workOrderId={wo.id}
          onClose={handleSrQuickViewClose}
          onUnlinked={handleSrUnlinked}
          translate={translate}
        />
      )}

      {/* Remove confirmation dialog */}
      <RemoveConfirmDialog
        open={!!removeDialog}
        title={
          removeDialog?.type === "assignee"
            ? translate("detail.remove.assigneeTitle")
            : removeDialog?.type === "machine"
            ? translate("detail.remove.machineTitle")
            : translate("detail.remove.locationTitle")
        }
        description={
          removeDialog?.type === "assignee"
            ? translate("detail.remove.assigneeDescription").replace("{{name}}", removeDialog?.name || "")
            : removeDialog?.type === "machine"
            ? translate("detail.remove.machineDescription").replace("{{name}}", removeDialog?.name || "")
            : translate("detail.remove.locationDescription").replace("{{name}}", removeDialog?.name || "")
        }
        icon={
          removeDialog?.type === "assignee" ? (
            <PersonRemoveIcon sx={{ fontSize: "24px", color: "#D4572B" }} />
          ) : removeDialog?.type === "machine" ? (
            <MachineIcon sx={{ fontSize: "24px", color: "#D4572B" }} />
          ) : (
            <LocationIcon sx={{ fontSize: "24px", color: "#D4572B" }} />
          )
        }
        confirmLabel={translate("detail.remove.confirmLabel")}
        cancelLabel={translate("detail.remove.cancelLabel")}
        removingLabel={translate("detail.removing")}
        onClose={() => setRemoveDialog(null)}
        onConfirm={handleRemoveConfirm}
      />

      {/* Toast notifications stack */}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />

      {/* Completion dialog (modal) */}
      <CompletionDialog
        open={showCompletionForm}
        workOrderId={wo?.id || ""}
        workOrderNumber={wo?.number || ""}
        hasMachine={!!wo?.machineId}
        hasLinkedSRs={!!(wo?.linkedServiceReports && wo.linkedServiceReports.length > 0)}
        machineName={wo?.machineName}
        onConfirm={handleCompletionConfirm}
        onCancel={() => { setShowCompletionForm(false); setCompletionNotes(""); setCost(""); setCreateSR(false); }}
      />

      {/* Edit closing details dialog — WO-WORKER-EDIT-NOTES.
          Reuses CompletionDialog in "edit" mode. Pre-fills current notes + cost.
          Submits via PATCH /{id}/completion-details (no status change). */}
      <CompletionDialog
        open={showEditDetailsDialog}
        workOrderId={wo?.id || ""}
        workOrderNumber={wo?.number || ""}
        hasMachine={!!wo?.machineId}
        hasLinkedSRs={!!(wo?.linkedServiceReports && wo.linkedServiceReports.length > 0)}
        machineName={wo?.machineName}
        mode="edit"
        initialNotes={wo?.completionNotes || ""}
        initialCost={wo?.cost ?? null}
        onConfirm={(notes, costVal) => handleEditDetailsConfirm(notes, costVal)}
        onCancel={() => setShowEditDetailsDialog(false)}
      />

      {/* Rework dialog (modal) — manager returns WO to worker */}
      <ReworkDialog
        open={showReworkDialog}
        workOrderId={wo?.id || ""}
        workOrderNumber={wo?.number || ""}
        onConfirm={handleReworkConfirm}
        onCancel={() => setShowReworkDialog(false)}
      />
    </Box>
  );
};

// ─── Shared card style ──────────────────────────────────────
const cardSx = {
  backgroundColor: p.white,
  border: `1px solid ${p.border}`,
  borderRadius: "12px",
  padding: "20px 24px",
};

// ─── Remove Confirm Dialog (Operations Danger Dialog pattern) ──

interface RemoveConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  icon: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  removingLabel?: string;
  loading?: boolean;
}

const removeDialogKeyframesId = "remove-confirm-dialog-keyframes";

const RemoveConfirmDialog: React.FC<RemoveConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  icon,
  confirmLabel,
  cancelLabel,
  removingLabel,
  loading,
}) => {
  useEffect(() => {
    if (!document.getElementById(removeDialogKeyframesId)) {
      const style = document.createElement("style");
      style.id = removeDialogKeyframesId;
      style.textContent = `
        @keyframes removeConfirmSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes removeConfirmBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(12,17,23,0.25)",
          animation: "removeConfirmBackdropIn 0.2s ease",
        }}
      />

      {/* Dialog card */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "420px",
          mx: "16px",
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
          animation: "removeConfirmSlideUp 0.3s ease",
        }}
      >
        {/* Terracotta accent bar */}
        <Box
          sx={{
            height: "4px",
            background: "linear-gradient(90deg, #D4572B 0%, #8B3A1E 100%)",
          }}
        />

        {/* Content */}
        <Box sx={{ padding: "32px 28px 24px", textAlign: "center" }}>
          {/* Icon circle */}
          <Box
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "rgba(212,87,43,0.08)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: "16px",
            }}
          >
            {icon}
          </Box>

          {/* Title */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "18px",
              fontWeight: 700,
              color: "#0C1117",
              mb: "8px",
            }}
          >
            {title}
          </Box>

          {/* Description */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: "#6B7280",
              lineHeight: 1.6,
              mb: "24px",
            }}
          >
            {description}
          </Box>

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "16px" }}>
            <Box
              onClick={onClose}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#8C9099",
                cursor: "pointer",
                padding: "8px 16px",
                borderRadius: "8px",
                transition: "color 0.15s ease",
                "&:hover": { color: "#4A4F57" },
              }}
            >
              {cancelLabel || "Cancel"}
            </Box>
            <Box
              onClick={loading ? undefined : onConfirm}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: loading ? "#D1CCC4" : "#D4572B",
                border: loading ? "1.5px solid #D1CCC4" : "1.5px solid #D4572B",
                backgroundColor: "transparent",
                borderRadius: "24px",
                padding: "9px 28px",
                cursor: loading ? "default" : "pointer",
                transition: "all 0.2s ease",
                "&:hover": loading
                  ? {}
                  : { backgroundColor: "rgba(212,87,43,0.06)" },
              }}
            >
              {loading ? (removingLabel || "Removing…") : confirmLabel}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Sub-components ──────────────────────────────────────────

const SectionHeader = ({ label, noMargin }: { label: string; noMargin?: boolean }) => (
  <Box sx={{
    fontFamily: dt.fontMono,
    fontSize: "11px",
    fontWeight: 500,
    color: p.muted,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: noMargin ? 0 : "14px",
  }}>
    {label}
  </Box>
);

interface DetailRowProps {
  icon?: React.ReactElement;
  label: string;
  value: React.ReactNode;
}

const DetailRow = ({ icon, label, value }: DetailRowProps) => (
  <Box sx={{
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "12px 0",
    borderBottom: `1px solid ${p.pageBg}`,
    "&:last-child": { borderBottom: "none" },
  }}>
    <Box sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "18px",
      height: "18px",
      flexShrink: 0,
      marginTop: "1px",
    }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{
        fontFamily: dt.font,
        fontSize: "11px",
        fontWeight: 500,
        letterSpacing: "0.04em",
        color: p.muted,
        marginBottom: "2px",
      }}>
        {label}
      </Box>
      {value}
    </Box>
  </Box>
);

interface LinkedItemProps {
  accentColor: string;
  icon: React.ReactElement;
  label: string;
  value: string;
  description?: string;
  extra?: React.ReactNode;
  onClick: () => void;
}

const LinkedItem = ({ accentColor, icon, label, value, description, extra, onClick }: LinkedItemProps) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      gap: "10px",
      padding: "10px 14px",
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: "8px",
      backgroundColor: p.warmCream,
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": { backgroundColor: "#EFEDE8" },
    }}
  >
    <Box sx={{ flexShrink: 0, marginTop: "2px" }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ fontFamily: dt.font, fontSize: "11px", color: p.muted }}>{label}</Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <Box sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 600, color: accentColor }}>
          {value}
        </Box>
        {extra}
      </Box>
      {description && (
        <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: p.body, marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {description}
        </Box>
      )}
    </Box>
  </Box>
);

// Inline assignee avatar for the detail card
const AVATAR_COLORS = [
  "#3498DB", "#E74C3C", "#2ECC71", "#9B59B6", "#F39C12",
  "#1ABC9C", "#E67E22", "#2980B9", "#C0392B", "#27AE60",
];

const AssigneeAvatar = ({ userId, userName, size = 24, isLead }: { userId: string; userName: string; size?: number; isLead?: boolean }) => {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  const color = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  const parts = userName.trim().split(/\s+/);
  const initials = parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : (parts[0]?.[0] || "?").toUpperCase();

  return (
    <Box sx={{
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: "50%",
      backgroundColor: color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: `${Math.round(size * 0.4)}px`,
      fontWeight: 700,
      fontFamily: dt.font,
      color: "#fff",
      flexShrink: 0,
      border: isLead ? "2px solid #F0C05A" : "2px solid #fff",
      boxSizing: "border-box",
    }}>
      {initials}
    </Box>
  );
};

// ─── Extracted Card Components ──────────────────────────────

/** Work Instructions card — terracotta left border, warm cream bg */
const WorkInstructionsCard = ({ wo, translate }: { wo: any; translate: (k: string) => string }) => {
  if (!wo.workInstructions) return null;
  return (
    <Box sx={{
      ...cardSx,
      borderLeft: `3px solid ${p.terracotta}`,
      backgroundColor: "#FDFCFA",
      animation: "cardReveal 0.4s ease 0.05s both",
    }}>
      <Box sx={{
        fontFamily: dt.fontMono,
        fontSize: "11px",
        fontWeight: 500,
        color: p.terracotta,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        marginBottom: "14px",
      }}>
        {translate("detail.workInstructions")}
      </Box>
      <Box sx={{
        fontFamily: dt.font,
        fontSize: "14px",
        color: p.body,
        lineHeight: "22px",
        whiteSpace: "pre-wrap",
      }}>
        {wo.workInstructions}
      </Box>
      <Box sx={{
        fontFamily: dt.font,
        fontSize: "12px",
        color: p.muted,
        marginTop: "12px",
        paddingTop: "10px",
        borderTop: `1px solid ${p.border}`,
      }}>
        {translate("detail.writtenBy")} <Box component="span" sx={{ fontWeight: 500 }}>{wo.createdByUserName || "Unknown"}</Box> &middot; {dayjs(wo.createdDate).format("D MMM YYYY")}
      </Box>
    </Box>
  );
};

/** Description / Additional Context card */
const DescriptionCard = ({ wo, hasSrs, translate }: { wo: any; hasSrs: boolean; translate: (k: string) => string }) => {
  if (hasSrs && !wo.description) return null;
  if (!wo.description) return null;

  const headerLabel = hasSrs
    ? translate("detail.additionalContext")
    : "Description";

  return (
    <Box sx={{ ...cardSx, animation: "cardReveal 0.4s ease 0.1s both" }}>
      <SectionHeader label={headerLabel} />
      <Box sx={{
        fontFamily: dt.font,
        fontSize: "14px",
        color: p.body,
        lineHeight: "22px",
        whiteSpace: "pre-wrap",
      }}>
        {wo.description}
      </Box>
    </Box>
  );
};

/** Timeline card — colored dots, status chips, JetBrains Mono timestamps, terracotta Post button */
const TimelineCard = ({ wo, canWrite, translate, updateText, setUpdateText, handleAddUpdate }: {
  wo: any; canWrite: boolean; translate: (k: string) => string;
  updateText: string; setUpdateText: (v: string) => void; handleAddUpdate: () => void;
}) => (
  <Box sx={{ ...cardSx, animation: "cardReveal 0.4s ease 0.2s both" }}>
    <SectionHeader label={translate("detail.progressTimeline")} />
    {wo.progressUpdates.length === 0 ? (
      <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: p.muted, padding: "12px 0" }}>
        {translate("empty.noUpdates")}
      </Box>
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "0" }}>
        {wo.progressUpdates.map((upd: any, idx: number) => {
          const dotColor = upd.newStatusId
            ? (woStatusColors[upd.newStatusId]?.text || p.muted)
            : p.amber;
          const isStatusChange = upd.oldStatusName && upd.newStatusName;
          const isLast = idx === wo.progressUpdates.length - 1;

          const getChipBg = (statusId?: number): string => {
            if (!statusId) return "#F4F6F9";
            const sc = woStatusColors[statusId];
            if (sc) return sc.bg;
            return "#F4F6F9";
          };
          const getChipColor = (statusId?: number): string => {
            if (!statusId) return p.body;
            const sc = woStatusColors[statusId];
            if (sc) return sc.text;
            return p.body;
          };

          return (
            <Box key={upd.id} sx={{
              display: "flex",
              gap: "14px",
              paddingBottom: "16px",
              "&:last-child": { paddingBottom: "0" },
            }}>
              {/* Dot + connector line */}
              <Box sx={{
                width: "10px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: "4px",
                flexShrink: 0,
              }}>
                <Box sx={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: dotColor,
                  flexShrink: 0,
                }} />
                {!isLast && (
                  <Box sx={{
                    width: "2px",
                    flexGrow: 1,
                    minHeight: "20px",
                    backgroundColor: p.border,
                  }} />
                )}
              </Box>
              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {isStatusChange && (
                  <Box sx={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "6px", flexWrap: "wrap" }}>
                    <Box sx={{
                      display: "inline-flex", alignItems: "center",
                      borderRadius: "4px", padding: "2px 10px",
                      fontSize: "11px", fontWeight: 600, fontFamily: dt.font,
                      backgroundColor: getChipBg(upd.oldStatusId),
                      color: getChipColor(upd.oldStatusId),
                    }}>
                      {upd.oldStatusName}
                    </Box>
                    <Box sx={{ fontSize: "10px", color: p.muted, fontFamily: dt.fontMono }}>&rarr;</Box>
                    <Box sx={{
                      display: "inline-flex", alignItems: "center",
                      borderRadius: "4px", padding: "2px 10px",
                      fontSize: "11px", fontWeight: 600, fontFamily: dt.font,
                      backgroundColor: getChipBg(upd.newStatusId),
                      color: getChipColor(upd.newStatusId),
                    }}>
                      {upd.newStatusName}
                    </Box>
                  </Box>
                )}
                {!isStatusChange && upd.updateText && (
                  <Box sx={{
                    display: "inline-flex", alignItems: "center",
                    borderRadius: "4px", padding: "2px 10px",
                    fontSize: "11px", fontWeight: 600, fontFamily: dt.font,
                    backgroundColor: "#FFF3E0", color: "#B8860B",
                    marginBottom: "6px",
                  }}>
                    {translate("detail.progressUpdateLabel")}
                  </Box>
                )}
                <Box sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  color: p.body,
                  lineHeight: "20px",
                }}>
                  {upd.updateText}
                </Box>
                <Box sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "11px",
                  fontWeight: 400,
                  color: p.muted,
                  marginTop: "4px",
                }}>
                  {upd.createdByUserName} &middot; {dayjs(upd.createdDate).format("MMM D, HH:mm")}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    )}

    {/* Add update input */}
    {canWrite && (
      <Box sx={{ display: "flex", gap: "10px", marginTop: "14px", alignItems: "center" }}>
        <TextField
          fullWidth
          size="small"
          placeholder={translate("detail.addProgressPlaceholder")}
          value={updateText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUpdateText(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddUpdate(); } }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: dt.font,
              fontSize: "13px",
              borderRadius: "8px",
              backgroundColor: p.warmCream,
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: p.muted },
              "&.Mui-focused fieldset": { borderColor: p.terracotta, borderWidth: "1.5px" },
            },
          }}
        />
        <button
          onClick={handleAddUpdate}
          disabled={!updateText.trim()}
          className={`min-w-[72px] px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer ${
            !updateText.trim()
              ? "bg-[#E8E5DF] text-[#8C9099] cursor-not-allowed"
              : "bg-[#D4572B] text-white hover:bg-[#B84A24]"
          }`}
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {translate("detail.postUpdate")}
        </button>
      </Box>
    )}
  </Box>
);

/** Attachments card */
const AttachmentsCard = ({ wo, canWrite, translate, handleFileUpload, handleFileDelete }: {
  wo: any; canWrite: boolean; translate: (k: string) => string;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileDelete: (fileId: string) => void;
}) => {
  // WO-CLOSING-PHOTOS-0414: only render General (non-closing-remarks) files here.
  // Closing-remarks photos now live in ClosingReportCard's carousel.
  const generalFiles: WorkOrderFile[] = (wo.files ?? []).filter(
    (f: WorkOrderFile) => !isClosingRemarksFile(f)
  );
  return (
    <Box sx={{ ...cardSx, animation: "cardReveal 0.4s ease 0.3s both" }}>
      <SectionHeader label={translate("detail.woAttachments")} />
      {generalFiles.length === 0 && !canWrite ? (
        <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: p.muted, padding: "8px 0" }}>
          {translate("empty.noFiles")}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {generalFiles.map((f: WorkOrderFile) => (
            <Box key={f.id} sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "10px 14px",
              backgroundColor: p.warmCream,
              borderRadius: "8px",
            }}>
              <FileIcon sx={{ fontSize: 18, color: p.terracotta }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 500, color: p.charcoal, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.fileName}
                </Box>
                <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", fontWeight: 400, color: p.muted }}>
                  {formatFileSize(f.fileSize)}
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  // WO-CLOSING-PHOTOS-0414 follow-up: window.open on the file
                  // URL issues a plain GET with no Authorization header, so it
                  // 401s silently. Fetch via authenticated axios and trigger a
                  // programmatic download instead.
                  void downloadAuthenticatedFile(
                    getWorkOrderFileUrl(f.id, true),
                    f.fileName
                  );
                }}
                sx={{ color: p.muted, "&:hover": { color: p.terracotta } }}
              >
                <DownloadIcon sx={{ fontSize: 16 }} />
              </IconButton>
              {canWrite && (
                <IconButton size="small" onClick={() => handleFileDelete(f.id)} sx={{ color: p.muted, "&:hover": { color: p.red } }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}
      {canWrite && (
        <Button
          component="label"
          startIcon={<AddIcon sx={{ fontSize: 16, color: p.terracotta }} />}
          sx={{
            marginTop: "10px",
            fontFamily: dt.font,
            fontSize: "13px",
            textTransform: "none",
            color: p.muted,
            width: "100%",
            border: `1.5px dashed ${p.border}`,
            borderRadius: "8px",
            padding: "12px",
            "&:hover": { borderColor: p.muted, backgroundColor: "rgba(0,0,0,0.01)" },
          }}
        >
          {translate("detail.addFile")}
          <input type="file" hidden onChange={handleFileUpload} />
        </Button>
      )}
    </Box>
  );
};

/** Actions card */
interface ActionsCardProps {
  wo: any;
  transitions: number[];
  canTransition: boolean;
  canVerify: boolean;
  canWrite: boolean;
  canManage?: boolean;
  isAssignedToMe: boolean;
  canEscalate: boolean;
  showCompletionForm: boolean;
  completionNotes: string;
  cost: string;
  createSR: boolean;
  translate: (key: string) => string;
  handleStatusChange: (s: number) => void;
  setShowCompletionForm: (v: boolean) => void;
  setCompletionNotes: (v: string) => void;
  setCost: (v: string) => void;
  setCreateSR: (v: boolean) => void;
  setShowEscalateDialog: (v: boolean) => void;
  setShowReworkDialog: (v: boolean) => void;
  onEdit?: () => void;
  /** WO-WORKER-EDIT-NOTES: opens CompletionDialog in edit mode on a
   *  PendingReview WO. Only rendered for the assigned worker or manager. */
  onEditClosingDetails?: () => void;
  isPhone: boolean;
  isTablet?: boolean;
}

const ActionsCard: React.FC<ActionsCardProps> = ({
  wo, transitions, canTransition, canVerify, canWrite, canManage, isAssignedToMe,
  canEscalate, showCompletionForm, completionNotes, cost, createSR,
  translate, handleStatusChange,
  setShowCompletionForm, setCompletionNotes, setCost, setCreateSR,
  setShowEscalateDialog, setShowReworkDialog, onEdit, onEditClosingDetails,
  isPhone, isTablet,
}) => (
  <Box sx={{ ...cardSx, animation: "cardReveal 0.4s ease 0.15s both" }}>
    <SectionHeader label={translate("detail.sectionActions")} />
    {/* Edit button — top of actions */}
    {canManage && !showCompletionForm && onEdit && (
      <Box sx={{ marginBottom: "8px" }}>
        <button
          onClick={onEdit}
          className="w-full rounded-lg px-5 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer text-center bg-transparent text-[#4A4F57] border-[1.5px] border-[#E8E5DF] hover:bg-black/[0.02] flex items-center justify-center gap-1.5"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          <EditIcon sx={{ fontSize: 16 }} />
          {isPhone ? translate("actions.editShort") : translate("actions.editWorkOrder")}
        </button>
      </Box>
    )}
    {/* Edit Closing Details button — WO-WORKER-EDIT-NOTES.
        Visible to the assigned worker OR any manager while PendingReview, so a
        typo or missing cost can be corrected before Confirm Completed without
        a full Return-for-Rework cycle. Neutral outlined style: this is a
        utility action, not primary (green) or destructive (amber). */}
    {wo.workOrderStatusId === WorkOrderStatusCode.PendingReview &&
      (isAssignedToMe || canManage) &&
      !showCompletionForm &&
      onEditClosingDetails && (
        <Box sx={{ marginBottom: "8px" }}>
          <button
            onClick={onEditClosingDetails}
            className="w-full rounded-lg px-5 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer text-center bg-transparent text-[#4A4F57] border-[1.5px] border-[#E8E5DF] hover:bg-black/[0.02] flex items-center justify-center gap-1.5"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
            {isPhone
              ? translate("actions.editDetailsShort")
              : translate("actions.editClosingDetails")}
          </button>
        </Box>
      )}
    {/* Confirm Completed button - managers only, when PendingReview */}
    {wo.workOrderStatusId === 9 && canManage && !showCompletionForm && (
      <Box sx={{ marginBottom: "8px" }}>
        <button
          onClick={() => handleStatusChange(WorkOrderStatusCode.Completed)}
          className="w-full rounded-lg px-5 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer text-center text-white flex items-center justify-center gap-1.5"
          style={{ fontFamily: "'Sora', sans-serif", background: "linear-gradient(135deg, #2D8C4E 0%, #1a6b3a 100%)", borderRadius: "8px", border: "none" }}
        >
          <CheckIcon sx={{ fontSize: 16 }} />
          {translate("board.confirmCompleted")}
        </button>
      </Box>
    )}
    {/* Return for Rework button - managers only, when PendingReview */}
    {wo.workOrderStatusId === 9 && canManage && !showCompletionForm && (
      <Box sx={{ marginBottom: "8px" }}>
        <button
          onClick={() => setShowReworkDialog(true)}
          className="w-full rounded-lg px-5 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer text-center flex items-center justify-center gap-1.5"
          style={{
            fontFamily: "'Sora', sans-serif",
            background: "transparent",
            color: "#E8A817",
            border: "1.5px solid #E8A817",
            borderRadius: "8px",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(232,168,23,0.05)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
        >
          {translate("board.returnForRework")}
        </button>
      </Box>
    )}
    {/* Transition buttons — CompletionDialog modal handles PendingReview.
        All action buttons render in this in-flow Actions card at every viewport
        (desktop / tablet / phone). Labels come from the translated `actions.*`
        keys so the card is fully localized (en / cs / sk). */}
    <Box sx={{ display: "flex", flexDirection: (isPhone || isTablet) ? "row" : "column", gap: "8px", flexWrap: "wrap" }}>
      {transitions
        .filter((t) => {
          if (!canTransition) return false;
          // When in PendingReview, dedicated buttons handle Completed (Confirm Completed)
          // and InProgress (Return for Rework). Keep Cancelled visible.
          if (wo.workOrderStatusId === WorkOrderStatusCode.PendingReview) {
            if (t === WorkOrderStatusCode.Completed || t === WorkOrderStatusCode.InProgress) return false;
          }
          // Start Work (InProgress) and Submit for Review (PendingReview) are worker-facing
          // actions — only assigned workers should see them. Managers bypass the assignment check.
          if ((t === WorkOrderStatusCode.InProgress || t === WorkOrderStatusCode.PendingReview) && !isAssignedToMe && !canManage) return false;
          return true;
        })
        .map((t) => {
          const btnClass = getActionButtonClass(t);
          const labelKey = (isPhone ? statusActionLabelKeysPhone : statusActionLabelKeys)[t];
          const label = labelKey ? translate(labelKey) : translate("actions.changeStatus");
          return (
            <button
              key={t}
              onClick={() => handleStatusChange(t)}
              className={`${btnClass} ${(isPhone || isTablet) ? "flex-1 min-w-0" : ""} ${t === WorkOrderStatusCode.PendingReview ? "py-3" : ""} flex items-center justify-center gap-1.5`}
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {t === WorkOrderStatusCode.PendingReview && <CheckIcon sx={{ fontSize: 16 }} />}
              {t === WorkOrderStatusCode.InProgress && <PlayIcon sx={{ fontSize: 16 }} />}
              {t === WorkOrderStatusCode.OnHold && <PauseIcon sx={{ fontSize: 16 }} />}
              {t === WorkOrderStatusCode.Completed && <CheckIcon sx={{ fontSize: 16 }} />}
              {label}
            </button>
          );
        })}
      {transitions.length === 0 && !canTransition && (
        <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: p.muted }}>{translate("detail.noActionsAvailable")}</Box>
      )}
    </Box>

    {/* Escalate button */}
    {canEscalate && (
      <Button
        onClick={() => setShowEscalateDialog(true)}
        sx={{
          marginTop: "4px",
          fontFamily: dt.font,
          fontSize: "13px",
          textTransform: "none",
          color: p.amber,
          width: "100%",
          "&:hover": { backgroundColor: "rgba(232,168,23,0.06)" },
        }}
      >
        {translate("escalation.title")}
      </Button>
    )}
  </Box>
);

// WO-CLOSING-PHOTOS-0414: same image-extension regex the SrAttachments component
// uses inside IncidentContextCard. Kept local here to avoid a cross-module import.
const CLOSING_IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;
const isClosingImageFile = (name: string): boolean =>
  CLOSING_IMAGE_EXTENSIONS.test(name || "");

/** Closing Report card - shown when WO has completionNotes and status is PendingReview/Completed */
const ClosingReportCard = ({ wo, translate }: { wo: any; translate: (key: string) => string }) => {
  // WO-CLOSING-PHOTOS-0414: split closing-remarks files into images (carousel)
  // and non-images (chip row).
  const closingFiles: WorkOrderFile[] = (wo.files ?? []).filter((f: WorkOrderFile) =>
    isClosingRemarksFile(f)
  );
  const closingImages = useMemo(
    () => closingFiles.filter(f => isClosingImageFile(f.fileName)),
    [closingFiles]
  );
  const closingNonImages = useMemo(
    () => closingFiles.filter(f => !isClosingImageFile(f.fileName)),
    [closingFiles]
  );
  const closingImageUrls = useMemo(
    () => closingImages.map(f => getWorkOrderFileUrl(f.id)),
    [closingImages]
  );
  // WO-CLOSING-PHOTOS-0414 follow-up: the file endpoint requires a Bearer
  // token that `<img src>` cannot send. Pre-fetch each image via authenticated
  // axios and render from `blob:` URLs instead. Blobs are cached + ref-counted
  // so reopening the lightbox reuses the same object URL.
  const { urls: resolvedBlobUrls } = useAuthenticatedBlobUrls(closingImageUrls);
  const carouselUrls = useMemo(
    () => resolvedBlobUrls.filter((u): u is string => !!u),
    [resolvedBlobUrls]
  );
  const galleryImages = useGalleryImages({ type: "urls", urls: carouselUrls });

  // Render guard must run AFTER hooks (useMemo / useGalleryImages) — otherwise
  // the hook order changes between renders and React throws.
  if (!wo.completionNotes || ![6, 7, 9].includes(wo.workOrderStatusId)) return null;

  const formatDuration = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
  };

  return (
    <Box
      sx={{
        ...cardSx,
        borderLeft: "4px solid #2D8C4E",
        backgroundColor: "#F0FFF5",
        animation: "cardReveal 0.4s ease 0.1s both",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
        <CheckIcon sx={{ fontSize: 16, color: "#2D8C4E" }} />
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "11px",
            fontWeight: 700,
            color: "#2D8C4E",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {translate("board.closingReport")}
        </Box>
      </Box>
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "13px",
          color: p.charcoal,
          lineHeight: "20px",
          whiteSpace: "pre-wrap",
          marginBottom: (wo.cost || wo.actualDurationMinutes) ? "12px" : "0",
        }}
      >
        {wo.completionNotes}
      </Box>
      {/* Optional cost and duration lines */}
      {(wo.cost != null || wo.actualDurationMinutes != null) && (
        <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: (closingFiles.length > 0 || wo.assignedToUserName) ? "12px" : "0" }}>
          {wo.cost != null && (
            <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: p.muted }}>
              {translate("fields.cost")}: <Box component="span" sx={{ fontWeight: 600, color: p.body }}>{wo.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Box>
            </Box>
          )}
          {wo.actualDurationMinutes != null && (
            <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: p.muted }}>
              {translate("board.duration")}: <Box component="span" sx={{ fontWeight: 600, color: p.body }}>{formatDuration(wo.actualDurationMinutes)}</Box>
            </Box>
          )}
        </Box>
      )}
      {/* WO-CLOSING-PHOTOS-0414: closing-remarks attachments — images as
          carousel (click → full-screen lightbox), non-images as chip row with
          download links. Same visual pattern as SrAttachments in
          IncidentContextCard. */}
      {closingFiles.length > 0 && (
        <Box sx={{ marginBottom: wo.assignedToUserName ? "12px" : "0" }}>
          <Box
            sx={{
              fontFamily: dt.fontMono,
              fontSize: "10px",
              fontWeight: 600,
              color: "#2D8C4E",
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              marginBottom: "8px",
            }}
          >
            {translate("board.closingRemarksPhotos")}
          </Box>
          {galleryImages.length > 0 && (
            <Box sx={{ marginBottom: closingNonImages.length > 0 ? "10px" : 0 }}>
              <ImagePreviewStrip
                images={galleryImages}
                maxVisible={4}
                thumbnailSize={64}
                gap={8}
                borderRadius="8px"
              />
            </Box>
          )}
          {closingNonImages.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {closingNonImages.map((f: WorkOrderFile) => (
                <Box
                  key={f.id}
                  component="button"
                  type="button"
                  onClick={() => {
                    // WO-CLOSING-PHOTOS-0414 follow-up: plain <a href> can't
                    // send the Bearer token, so we fetch the file via
                    // authenticated axios and trigger a programmatic download.
                    void downloadAuthenticatedFile(
                      getWorkOrderFileUrl(f.id, true),
                      f.fileName
                    );
                  }}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 12px",
                    backgroundColor: p.warmCream,
                    border: `1px solid ${p.border}`,
                    borderRadius: "8px",
                    fontSize: "12px",
                    fontFamily: dt.font,
                    color: p.charcoal,
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "border-color 0.2s ease, color 0.2s ease",
                    "&:hover": { borderColor: p.terracotta, color: p.terracotta },
                  }}
                >
                  <FileIcon sx={{ fontSize: 14, color: p.terracotta }} />
                  <Box sx={{ fontWeight: 400 }}>{f.fileName}</Box>
                  {f.fileSize > 0 && (
                    <Box sx={{ fontFamily: dt.fontMono, fontSize: "10px", color: p.muted, lineHeight: "12px" }}>
                      {formatFileSize(f.fileSize)}
                    </Box>
                  )}
                  <DownloadIcon sx={{ fontSize: 14, color: p.muted }} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
      {/* Footer: completed by + date */}
      {wo.assignedToUserName && (
        <Box sx={{ fontFamily: dt.font, fontSize: "11px", color: p.muted, borderTop: "1px solid #D4E8DB", paddingTop: "8px" }}>
          {translate("board.completedBy")} {wo.assignedToUserName}
          {wo.actualEndDate && " · " + new Date(wo.actualEndDate).toLocaleDateString()}
        </Box>
      )}
    </Box>
  );
};

/** Details card — Machine, Location, Assignees, Team, Due Date, Created by */
const DetailsCard = ({ wo, teamName, navigate, isPhone, canManage, translate, onRemoveAssignee, onRemoveMachine, onRemoveLocation }: {
  wo: any; teamName?: string; navigate: (path: string) => void; isPhone?: boolean;
  canManage?: boolean;
  translate: (key: string) => string;
  onRemoveAssignee?: (userId: string, userName: string) => void;
  onRemoveMachine?: () => void;
  onRemoveLocation?: () => void;
}) => (
  <Box sx={{ ...cardSx, animation: "cardReveal 0.4s ease 0.25s both" }}>
    <SectionHeader label={translate("detail.sectionDetails")} />
    <Box sx={{ display: "flex", flexDirection: "column", gap: "0" }}>
      {/* Machine */}
      <DetailRow
        icon={<MachineIcon sx={{ fontSize: 16, color: p.muted }} />}
        label={translate("detail.fieldMachine")}
        value={
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
                {wo.machineName || "\u2014"}
              </Box>
              {wo.machineSerialNumber && (
                <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", color: p.muted }}>
                  SN: {wo.machineSerialNumber}
                </Box>
              )}
            </Box>
            {canManage && wo.machineId && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onRemoveMachine?.(); }}
                sx={{
                  color: p.muted, width: "20px", height: "20px",
                  opacity: 0.5, transition: "all 0.15s ease",
                  "&:hover": { color: p.red, opacity: 1 },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        }
      />
      {/* Location with pin icon */}
      {(wo.locationName || wo.parentLocationName) && (
        <DetailRow
          icon={<LocationIcon sx={{ fontSize: 16, color: p.terracotta }} />}
          label={translate("detail.fieldLocation")}
          value={
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box sx={{ flex: 1, fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
                {wo.locationName}{wo.parentLocationName ? `, ${wo.parentLocationName}` : ""}
              </Box>
              {canManage && wo.locationId && (
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onRemoveLocation?.(); }}
                  sx={{
                    color: p.muted, width: "20px", height: "20px",
                    opacity: 0.5, transition: "all 0.15s ease",
                    "&:hover": { color: p.red, opacity: 1 },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          }
        />
      )}
      {/* Assignees — avatar + name per assignee */}
      <DetailRow
        icon={<PersonIcon sx={{ fontSize: 16, color: p.muted }} />}
        label={translate("detail.fieldAssignees")}
        value={
          wo.assignees && wo.assignees.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {wo.assignees.map((a: any) => (
                <Box key={a.userId} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <AssigneeAvatar userId={a.userId} userName={a.userName} size={24} isLead={a.role === 2} />
                  <Box sx={{ flex: 1, fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>{a.userName}</Box>
                  {canManage && (
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onRemoveAssignee?.(a.userId, a.userName); }}
                      sx={{
                        color: p.muted, width: "20px", height: "20px",
                        opacity: 0.5, transition: "all 0.15s ease",
                        "&:hover": { color: p.red, opacity: 1 },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
              {wo.assignedToUserName || translate("detail.unassigned")}
            </Box>
          )
        }
      />
      {/* Team + Due date — side-by-side on phone, stacked otherwise */}
      {isPhone ? (
        <Box sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0 16px",
          padding: "12px 0",
          borderBottom: `1px solid ${p.pageBg}`,
        }}>
          {/* Team */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", flexShrink: 0, marginTop: "1px" }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: p.terracotta, flexShrink: 0 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 500, letterSpacing: "0.04em", color: p.muted, marginBottom: "2px" }}>{translate("detail.fieldTeam")}</Box>
              <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
                {teamName || "\u2014"}
              </Box>
            </Box>
          </Box>
          {/* Due date */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", flexShrink: 0, marginTop: "1px" }}>
              <CalendarIcon sx={{ fontSize: 16, color: p.muted }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 500, letterSpacing: "0.04em", color: p.muted, marginBottom: "2px" }}>{translate("detail.fieldDue")}</Box>
              <Box sx={{ fontFamily: dt.fontMono, fontSize: "14px", fontWeight: 500, color: wo.dueDate ? p.blue : p.muted }}>
                {wo.dueDate ? dayjs(wo.dueDate).format("MMM D, YYYY") : "\u2014"}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          {/* Team with colored dot */}
          {teamName && (
            <DetailRow
              icon={<Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: p.terracotta, flexShrink: 0 }} />}
              label={translate("detail.fieldTeam")}
              value={
                <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
                  {teamName}
                </Box>
              }
            />
          )}
          {/* Due date in blue JetBrains Mono */}
          <DetailRow
            icon={<CalendarIcon sx={{ fontSize: 16, color: p.muted }} />}
            label={translate("detail.fieldDueDate")}
            value={
              <Box sx={{ fontFamily: dt.fontMono, fontSize: "14px", fontWeight: 500, color: wo.dueDate ? p.blue : p.muted }}>
                {wo.dueDate ? dayjs(wo.dueDate).format("MMM D, YYYY") : "\u2014"}
              </Box>
            }
          />
        </>
      )}
      {/* Created by */}
      <DetailRow
        icon={<PersonIcon sx={{ fontSize: 16, color: p.muted }} />}
        label={translate("detail.fieldCreatedBy")}
        value={
          <Box>
            <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
              {wo.createdByUserName || "\u2014"}
            </Box>
            <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", color: p.muted }}>
              {dayjs(wo.createdDate).format("MMM D, YYYY HH:mm")}
            </Box>
          </Box>
        }
      />
      {/* Cost (if completed) */}
      {wo.cost != null && (
        <DetailRow
          icon={<Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: dt.fontMono, fontSize: "12px", color: p.muted, fontWeight: 700 }}>$</Box>}
          label={translate("detail.fieldCost")}
          value={
            <Box sx={{ fontFamily: dt.fontMono, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
              {wo.cost.toFixed(2)}
            </Box>
          }
        />
      )}
    </Box>
  </Box>
);

/** Linked Items card — SRs (on phone), RFS, parent WO with number/title/status, child WOs */
const LinkedItemsCard = ({ wo, navigate, translate, isPhone, onSrClick }: {
  wo: any; navigate: (path: string) => void; translate: (k: string) => string;
  isPhone: boolean; onSrClick?: (srId: string, srNumber?: string) => void;
}) => {
  const hasLinkedSrs = isPhone && wo.linkedServiceReports && wo.linkedServiceReports.length > 0;
  const hasLinkedItems =
    hasLinkedSrs ||
    wo.serviceRequestId ||
    wo.parentWorkOrderId ||
    (wo.childWorkOrders && wo.childWorkOrders.length > 0);
  if (!hasLinkedItems) return null;

  return (
    <>
      <Box sx={{ ...cardSx, animation: "cardReveal 0.4s ease 0.35s both" }}>
        <SectionHeader label={translate("detail.sectionLinkedItems")} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {/* Phone: show linked SRs here (desktop has them in IncidentContextCard) */}
          {isPhone && wo.linkedServiceReports && wo.linkedServiceReports.map((sr: any) => (
            <LinkedItem
              key={sr.serviceReportId}
              accentColor={p.terracotta}
              icon={<SrIcon sx={{ fontSize: 14, color: p.terracotta }} />}
              label={translate("detail.linkedCreatedFrom")}
              value={sr.serviceReportNumber ? `${translate("detail.linkedServiceReport")} ${sr.serviceReportNumber}` : translate("detail.linkedServiceReport")}
              onClick={() => onSrClick?.(sr.serviceReportId, sr.serviceReportNumber)}
            />
          ))}
          {/* Escalated to Service Hub */}
          {wo.serviceRequestId && (
            <LinkedItem
              accentColor={p.blue}
              icon={<LaunchOutlined sx={{ fontSize: 14, color: p.blue }} />}
              label={translate("detail.linkedEscalatedTo")}
              value={`RFS-${wo.serviceRequestId.substring(0, 5).toUpperCase()}`}
              description={wo.serviceRequestTitle}
              onClick={() => navigate(`/servicing/my-service-requests/${wo.serviceRequestId}`)}
            />
          )}
          {/* Follow-up from parent WO — use parentWorkOrderNumber/Title if available */}
          {wo.parentWorkOrderId && (
            <LinkedItem
              accentColor={p.muted}
              icon={<TreeIcon sx={{ fontSize: 14, color: p.muted }} />}
              label={translate("detail.linkedFollowUpFrom")}
              value={wo.parentWorkOrderNumber || `WO-${wo.parentWorkOrderId.substring(0, 13)}`}
              description={wo.parentWorkOrderTitle}
              extra={
                wo.parentWorkOrderStatusId ? (
                  <WorkOrderStatusBadge statusId={wo.parentWorkOrderStatusId} />
                ) : undefined
              }
              onClick={() => navigate(`/operations/work-orders/${wo.parentWorkOrderId}`)}
            />
          )}
          {/* Child work orders */}
          {wo.childWorkOrders && wo.childWorkOrders.length > 0 && wo.childWorkOrders.map((child: any) => (
            <LinkedItem
              key={child.id}
              accentColor={p.green}
              icon={<TreeIcon sx={{ fontSize: 14, color: p.green }} />}
              label={(wo.childWorkOrders.length === 1
                ? translate("detail.linkedHasFollowUps_one")
                : translate("detail.linkedHasFollowUps_other")
              ).replace("{{count}}", String(wo.childWorkOrders.length))}
              value={child.number || `WO-${child.id.substring(0, 13)}`}
              description={child.title}
              extra={
                <WorkOrderStatusBadge statusId={child.workOrderStatusId} statusName={child.workOrderStatusName} />
              }
              onClick={() => navigate(`/operations/work-orders/${child.id}`)}
            />
          ))}
        </Box>
      </Box>

      {/* Already-escalated badge (desktop only) */}
      {wo.serviceRequestId && !isPhone && (
        <Box
          sx={{
            ...cardSx,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            animation: "cardReveal 0.4s ease 0.42s both",
          }}
        >
          <LaunchOutlined sx={{ fontSize: 16, color: p.amber }} />
          <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: p.muted }}>
            {translate("escalation.alreadyEscalated")}:
          </Box>
          <Link
            to={`/servicing/my-service-requests/${wo.serviceRequestId}`}
            style={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: p.blue,
              textDecoration: "none",
            }}
          >
            {translate("escalation.viewOnServiceBoard")}
          </Link>
        </Box>
      )}
    </>
  );
};

// ─── Tablet Info Strip ──────────────────────────────────────
/** Horizontal strip showing Machine | Assigned | Team | Location below header */
const TabletInfoStrip = ({ wo, teamName, translate }: {
  wo: any; teamName?: string; translate: (key: string) => string;
}) => {
  const leadAssignee = wo.assignees?.find((a: any) => a.role === 2);
  const firstAssignee = leadAssignee || wo.assignees?.[0];
  const assigneeName = firstAssignee?.userName || wo.assignedToUserName;

  const items: { icon: React.ReactElement; label: string; value: string }[] = [];

  if (wo.machineName) {
    items.push({
      icon: <MachineIcon sx={{ fontSize: 14, color: p.muted }} />,
      label: translate("detail.infoStrip.machine"),
      value: wo.machineName,
    });
  }

  if (assigneeName) {
    items.push({
      icon: <PersonIcon sx={{ fontSize: 14, color: p.muted }} />,
      label: translate("detail.infoStrip.assigned"),
      value: assigneeName,
    });
  }

  if (teamName) {
    items.push({
      icon: <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: p.terracotta, flexShrink: 0, marginTop: "1px" }} />,
      label: translate("detail.infoStrip.team"),
      value: teamName,
    });
  }

  if (wo.locationName || wo.parentLocationName) {
    items.push({
      icon: <LocationIcon sx={{ fontSize: 14, color: p.terracotta }} />,
      label: translate("detail.infoStrip.location"),
      value: wo.locationName
        ? `${wo.locationName}${wo.parentLocationName ? `, ${wo.parentLocationName}` : ""}`
        : wo.parentLocationName || "",
    });
  }

  if (items.length === 0) return null;

  return (
    <Box sx={{
      backgroundColor: p.warmCream,
      borderBottom: `1px solid ${p.border}`,
      padding: "10px 24px",
    }}>
      <Box sx={{
        maxWidth: "1320px",
        margin: "0 auto",
        display: "flex",
        alignItems: "center",
        gap: "0",
      }}>
        {items.map((item, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <Box sx={{ width: "1px", height: "28px", backgroundColor: p.border, flexShrink: 0, margin: "0 16px" }} />
            )}
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flex: 1,
              minWidth: 0,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                {item.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "10px",
                  fontWeight: 500,
                  color: p.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  lineHeight: 1,
                }}>
                  {item.label}
                </Box>
                <Box sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: p.charcoal,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {item.value}
                </Box>
              </Box>
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

// ─── ALL DETAILS Accordion (tablet only) ────────────────────
/** Collapsible accordion for the Details card content on tablet */
const AllDetailsAccordion = ({ wo, teamName, navigate, translate, canManage, canWrite, onCancelAllFuture, onEditAllFuture, onRemoveAssignee, onRemoveMachine, onRemoveLocation }: {
  wo: any; teamName?: string; navigate: (path: string) => void;
  translate: (key: string) => string;
  canManage: boolean; canWrite: boolean;
  onCancelAllFuture: () => void;
  onEditAllFuture?: (woId: string) => void;
  onRemoveAssignee?: (userId: string, userName: string) => void;
  onRemoveMachine?: () => void;
  onRemoveLocation?: () => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ ...cardSx, padding: 0, animation: "cardReveal 0.4s ease 0.25s both" }}>
      {/* Accordion header */}
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          cursor: "pointer",
          userSelect: "none",
          transition: "background-color 0.15s ease",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.015)" },
        }}
      >
        <Box sx={{
          fontFamily: dt.fontMono,
          fontSize: "11px",
          fontWeight: 500,
          color: p.muted,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
        }}>
          {translate("detail.allDetails")}
        </Box>
        <ExpandMoreIcon
          sx={{
            fontSize: 20,
            color: p.muted,
            transition: "transform 0.25s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      {/* Accordion body */}
      <Collapse in={open}>
        <Box sx={{ padding: "0 24px 20px", borderTop: `1px solid ${p.pageBg}` }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0", paddingTop: "4px" }}>
            {/* Machine */}
            <DetailRow
              icon={<MachineIcon sx={{ fontSize: 16, color: p.muted }} />}
              label={translate("detail.fieldMachine")}
              value={
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
                      {wo.machineName || "\u2014"}
                    </Box>
                    {wo.machineSerialNumber && (
                      <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", color: p.muted }}>
                        SN: {wo.machineSerialNumber}
                      </Box>
                    )}
                  </Box>
                  {canManage && wo.machineId && (
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onRemoveMachine?.(); }}
                      sx={{
                        color: p.muted, width: "20px", height: "20px",
                        opacity: 0.5, transition: "all 0.15s ease",
                        "&:hover": { color: p.red, opacity: 1 },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
              }
            />
            {/* Location */}
            {(wo.locationName || wo.parentLocationName) && (
              <DetailRow
                icon={<LocationIcon sx={{ fontSize: 16, color: p.terracotta }} />}
                label={translate("detail.fieldLocation")}
                value={
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Box sx={{ flex: 1, fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
                      {wo.locationName}{wo.parentLocationName ? `, ${wo.parentLocationName}` : ""}
                    </Box>
                    {canManage && wo.locationId && (
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onRemoveLocation?.(); }}
                        sx={{
                          color: p.muted, width: "20px", height: "20px",
                          opacity: 0.5, transition: "all 0.15s ease",
                          "&:hover": { color: p.red, opacity: 1 },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                }
              />
            )}
            {/* Assignees */}
            <DetailRow
              icon={<PersonIcon sx={{ fontSize: 16, color: p.muted }} />}
              label={translate("detail.fieldAssignees")}
              value={
                wo.assignees && wo.assignees.length > 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {wo.assignees.map((a: any) => (
                      <Box key={a.userId} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <AssigneeAvatar userId={a.userId} userName={a.userName} size={24} isLead={a.role === 2} />
                        <Box sx={{ flex: 1, fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>{a.userName}</Box>
                        {canManage && (
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onRemoveAssignee?.(a.userId, a.userName); }}
                            sx={{
                              color: p.muted, width: "20px", height: "20px",
                              opacity: 0.5, transition: "all 0.15s ease",
                              "&:hover": { color: p.red, opacity: 1 },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
                    {wo.assignedToUserName || translate("detail.unassigned")}
                  </Box>
                )
              }
            />
            {/* Team */}
            {teamName && (
              <DetailRow
                icon={<Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: p.terracotta, flexShrink: 0 }} />}
                label={translate("detail.fieldTeam")}
                value={
                  <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
                    {teamName}
                  </Box>
                }
              />
            )}
            {/* Due date */}
            <DetailRow
              icon={<CalendarIcon sx={{ fontSize: 16, color: p.muted }} />}
              label={translate("detail.fieldDueDate")}
              value={
                <Box sx={{ fontFamily: dt.fontMono, fontSize: "14px", fontWeight: 500, color: wo.dueDate ? p.blue : p.muted }}>
                  {wo.dueDate ? dayjs(wo.dueDate).format("MMM D, YYYY") : "\u2014"}
                </Box>
              }
            />
            {/* Created by */}
            <DetailRow
              icon={<PersonIcon sx={{ fontSize: 16, color: p.muted }} />}
              label={translate("detail.fieldCreatedBy")}
              value={
                <Box>
                  <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
                    {wo.createdByUserName || "\u2014"}
                  </Box>
                  <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", color: p.muted }}>
                    {dayjs(wo.createdDate).format("MMM D, YYYY HH:mm")}
                  </Box>
                </Box>
              }
            />
            {/* Cost (if completed) */}
            {wo.cost != null && (
              <DetailRow
                icon={<Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: dt.fontMono, fontSize: "12px", color: p.muted, fontWeight: 700 }}>$</Box>}
                label={translate("detail.fieldCost")}
                value={
                  <Box sx={{ fontFamily: dt.fontMono, fontSize: "14px", fontWeight: 500, color: p.charcoal }}>
                    {wo.cost.toFixed(2)}
                  </Box>
                }
              />
            )}
          </Box>

          {/* Recurrence info inside accordion */}
          {(wo.recurrenceRule || wo.recurrenceParentId) && (
            <Box sx={{ marginTop: "16px", paddingTop: "12px", borderTop: `1px solid ${p.pageBg}` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                <RecurringIcon sx={{ fontSize: 16, color: p.blue }} />
                <SectionHeader
                  label={wo.recurrenceRule
                    ? translate("recurrence.recurringTemplate")
                    : translate("recurrence.recurringInstance")}
                  noMargin
                />
              </Box>
              {wo.recurrenceParentId && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <Box
                    onClick={() => navigate(`/operations/work-orders/${wo.recurrenceParentId}`)}
                    sx={{
                      display: "flex", alignItems: "center", gap: "6px",
                      color: p.blue, cursor: "pointer", fontFamily: dt.font, fontSize: "13px",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    <LinkIcon sx={{ fontSize: 14 }} />
                    {translate("recurrence.viewTemplate")}
                  </Box>
                  {canManage && (
                    <>
                      <Box
                        onClick={() => onEditAllFuture ? onEditAllFuture(wo.recurrenceParentId!) : navigate(`/operations/work-orders/${wo.recurrenceParentId}`)}
                        sx={{
                          display: "flex", alignItems: "center", gap: "6px",
                          color: p.blue, cursor: "pointer", fontFamily: dt.font, fontSize: "13px",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 14 }} />
                        {translate("recurringEdit.editAllFuture")}
                      </Box>
                      <Box
                        onClick={() => {
                          if (!window.confirm(translate("recurringEdit.cancelAllFutureConfirm"))) return;
                          onCancelAllFuture();
                        }}
                        sx={{
                          display: "flex", alignItems: "center", gap: "6px",
                          color: p.terracotta, cursor: "pointer", fontFamily: dt.font, fontSize: "13px",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        <LinkIcon sx={{ fontSize: 14 }} />
                        {translate("recurringEdit.cancelAllFuture")}
                      </Box>
                    </>
                  )}
                </Box>
              )}
              {wo.recurrenceRule && (
                <Box
                  onClick={() => navigate(`/operations/work-board?recurrenceParentId=${wo.id}`)}
                  sx={{
                    display: "flex", alignItems: "center", gap: "6px",
                    color: p.blue, cursor: "pointer", fontFamily: dt.font, fontSize: "13px",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <LinkIcon sx={{ fontSize: 14 }} />
                  {translate("recurrence.viewInstances")}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Right Column Component (desktop only) ─────────────────
interface RightColumnProps {
  wo: any;
  transitions: number[];
  canTransition: boolean;
  canVerify: boolean;
  canManage: boolean;
  canWrite: boolean;
  isAssignedToMe: boolean;
  canEscalate: boolean;
  showCompletionForm: boolean;
  completionNotes: string;
  cost: string;
  createSR: boolean;
  teamName?: string;
  translate: (key: string) => string;
  navigate: (path: string) => void;
  handleStatusChange: (s: number) => void;
  setShowCompletionForm: (v: boolean) => void;
  setCompletionNotes: (v: string) => void;
  setCost: (v: string) => void;
  setCreateSR: (v: boolean) => void;
  setShowEscalateDialog: (v: boolean) => void;
  setShowReworkDialog: (v: boolean) => void;
  onCancelAllFuture: () => void;
  onEditAllFuture?: (woId: string) => void;
  onSrClick?: (srId: string, srNumber?: string) => void;
  onEdit?: () => void;
  onEditClosingDetails?: () => void;
  onRemoveAssignee?: (userId: string, userName: string) => void;
  onRemoveMachine?: () => void;
  onRemoveLocation?: () => void;
  isPhone: boolean;
}

const RightColumn: React.FC<RightColumnProps> = ({
  wo, transitions, canTransition, canVerify, canManage, canWrite, isAssignedToMe,
  canEscalate, showCompletionForm, completionNotes, cost, createSR,
  teamName, translate, navigate, handleStatusChange,
  setShowCompletionForm, setCompletionNotes, setCost, setCreateSR,
  setShowEscalateDialog, setShowReworkDialog, onCancelAllFuture, onEditAllFuture, onSrClick,
  onEdit, onEditClosingDetails, onRemoveAssignee, onRemoveMachine, onRemoveLocation, isPhone,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
    <ActionsCard
      wo={wo}
      transitions={transitions}
      canTransition={canTransition}
      canVerify={canVerify}
      canWrite={canWrite}
      canManage={canManage}
      isAssignedToMe={isAssignedToMe}
      canEscalate={canEscalate}
      showCompletionForm={showCompletionForm}
      completionNotes={completionNotes}
      cost={cost}
      createSR={createSR}
      translate={translate}
      handleStatusChange={handleStatusChange}
      setShowCompletionForm={setShowCompletionForm}
      setCompletionNotes={setCompletionNotes}
      setCost={setCost}
      setCreateSR={setCreateSR}
      setShowEscalateDialog={setShowEscalateDialog}
      setShowReworkDialog={setShowReworkDialog}
      onEdit={onEdit}
      onEditClosingDetails={onEditClosingDetails}
      isPhone={isPhone}
    />
    <DetailsCard wo={wo} teamName={teamName} navigate={navigate} isPhone={false} canManage={canManage} translate={translate} onRemoveAssignee={onRemoveAssignee} onRemoveMachine={onRemoveMachine} onRemoveLocation={onRemoveLocation} />
    <LinkedItemsCard wo={wo} navigate={navigate} translate={translate} isPhone={isPhone} onSrClick={onSrClick} />

    {/* ── Recurrence card ───────────────────────────────── */}
    {(wo.recurrenceRule || wo.recurrenceParentId) && (
      <Box sx={{ ...cardSx, animation: "cardReveal 0.4s ease 0.4s both" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
          <RecurringIcon sx={{ fontSize: 16, color: p.blue }} />
          <SectionHeader
            label={wo.recurrenceRule
              ? translate("recurrence.recurringTemplate")
              : translate("recurrence.recurringInstance")}
            noMargin
          />
        </Box>
        {wo.recurrenceParentId && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Box
              onClick={() => navigate(`/operations/work-orders/${wo.recurrenceParentId}`)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: p.blue,
                cursor: "pointer",
                fontFamily: dt.font,
                fontSize: "13px",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <LinkIcon sx={{ fontSize: 14 }} />
              {translate("recurrence.viewTemplate")}
            </Box>
            {canManage && (
              <>
                <Box
                  onClick={() => onEditAllFuture ? onEditAllFuture(wo.recurrenceParentId!) : navigate(`/operations/work-orders/${wo.recurrenceParentId}`)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: p.blue,
                    cursor: "pointer",
                    fontFamily: dt.font,
                    fontSize: "13px",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                  {translate("recurringEdit.editAllFuture")}
                </Box>
                <Box
                  onClick={() => {
                    if (!window.confirm(translate("recurringEdit.cancelAllFutureConfirm"))) return;
                    onCancelAllFuture();
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: p.terracotta,
                    cursor: "pointer",
                    fontFamily: dt.font,
                    fontSize: "13px",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <LinkIcon sx={{ fontSize: 14 }} />
                  {translate("recurringEdit.cancelAllFuture")}
                </Box>
              </>
            )}
          </Box>
        )}
        {wo.recurrenceRule && (
          <Box
            onClick={() => navigate(`/operations/work-board?recurrenceParentId=${wo.id}`)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: p.blue,
              cursor: "pointer",
              fontFamily: dt.font,
              fontSize: "13px",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            <LinkIcon sx={{ fontSize: 14 }} />
            {translate("recurrence.viewInstances")}
          </Box>
        )}
      </Box>
    )}
  </Box>
);

export default WorkOrderDetailPage;
